import PageHeader from "@/components/PageHeader";
import Spinner from "@/components/Spinner";
import {
  Menubar,
  MenubarContent,
  MenubarItem,
  MenubarMenu,
  MenubarTrigger,
} from "@/components/ui/menubar";
import { useToast } from "@/components/ui/use-toast";
import { Module, PATH_CLIENTS, PATH_ROOT } from "@/constants";
import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";
import { ApiError } from "@/services/api";
import { ReadClient, WriteClient } from "@/types/client";
import { useMutation, useQuery } from "@tanstack/react-query";
import { exists } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ClientService } from "../service";
import ClientForm from "./ClientForm";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { useClientParams } from "../hooks/useClientParams";

export default function ClientEditor() {
  const { can_update } = useModulePermissions();
  const { t } = useTranslation();
  const params = useClientParams();
  const queryClient = () => ClientService.get(params.clientId);
  const { toast } = useToast();
  const { updateQueryDataForExistingItem } = useQueryCacheUpdater(
    Module.Clients,
  );
  const clientQuery = useQuery({
    queryKey: [Module.Clients, params.clientId],
    queryFn: queryClient,
    retry: false,
    staleTime: 15000,
    refetchOnWindowFocus: false,
  });

  const mutateClient = async (values: WriteClient): Promise<ReadClient> =>
    ClientService.patch(params.clientId, values);

  const clientMutation = useMutation<ReadClient, ApiError, WriteClient>({
    mutationFn: mutateClient,
    onSuccess: (result) => {
      toast({
        title: t("clients.success.update"),
      });
      updateQueryDataForExistingItem(result);
    },
    onError: ({ errorCode }) => {
      const description = exists(`errors.${errorCode}`)
        ? t(`errors.${errorCode}`)
        : t("errors.unknownError");
      toast({
        title: t("clients.errors.updateClient"),
        description,
        variant: "destructive",
      });
    },
  });

  const breadcrumbItems = useMemo(
    () => [
      { label: t("breadcrumb.home"), href: PATH_ROOT },
      { label: t("clients.title.multiple"), href: PATH_CLIENTS },
      { label: t("clients.edit", clientQuery.data ?? {}) },
    ],
    [t, clientQuery.data],
  );

  if (clientQuery.isLoading) {
    return <Spinner label={t("clients.loading")} className="w-full h-full " />;
  }

  return (
    <>
      <PageHeader breadcrumbItems={breadcrumbItems} />
      <Menubar className="mb-2">
        <MenubarMenu>
          <MenubarTrigger>{t("clients.menubar.client.trigger")}</MenubarTrigger>
          <MenubarContent>
            <MenubarItem disabled>
              {t("clients.menubar.client.showProjects")}
            </MenubarItem>
          </MenubarContent>
        </MenubarMenu>
      </Menubar>
      <ClientForm
        client={clientQuery.data}
        onSubmit={clientMutation.mutateAsync}
        disabled={clientMutation.isPending || !can_update}
      />
    </>
  );
}
